import * as React from 'react'

import { Spinner } from '@ps/ds/components/primitives/Spinner/Spinner'
import { cn } from '@ps/ds/lib/utils'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1 whitespace-nowrap font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:stroke-[1.75] relative',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        secondary: 'bg-transparent text-primary hover:bg-secondary border border-primary',
        muted:
          'bg-transparent text-primary hover:bg-accent hover:text-accent-foreground border border-input',
        ghost: 'text-primary hover:bg-secondary',
        link: 'text-primary underline-offset-4 underline hover:text-primary-hover',
      },
      size: {
        md: 'px-3 py-2.5 h-[41px] text-base min-w-20',
        sm: 'px-2.5 py-2 h-9 text-sm min-w-20',
        xxs: 'px-1 py-0.5 text-xs',
        'icon-md': 'size-[41px]',
        'icon-sm': 'size-9',
        'icon-xs': 'size-7',
        'icon-xxs': 'size-5',
      },
      rounded: {
        true: 'rounded-full',
        false: 'rounded-md',
      },
    },
    compoundVariants: [
      {
        variant: 'link',
        className: 'p-0 h-auto min-h-5 rounded-md',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      rounded: false,
    },
  },
)
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  loading?: boolean
}

const getIconSize = (size: ButtonProps['size']) => {
  switch (size) {
    case 'icon-md':
      return '[&_svg]:size-6'
    case 'md':
    case 'icon-sm':
    case 'icon-xs':
      return '[&_svg]:size-5'
    case 'sm':
    case 'xxs':
    case 'icon-xxs':
      return '[&_svg]:size-4'
    default:
      return '[&_svg]:size-5'
  }
}

const IconWrapper = ({
  children,
  size,
}: {
  children: React.ReactNode
  size: ButtonProps['size']
}) => {
  const iconSize = getIconSize(size)

  if (!React.isValidElement(children)) return children

  return (
    <span className={cn('pointer-events-none shrink-0 stroke-[1.75]', iconSize)}>{children}</span>
  )
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      asChild = false,
      children,
      leadingIcon,
      trailingIcon,
      loading = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    const startIcon = loading ? (
      <IconWrapper size={size}>
        <Spinner />
      </IconWrapper>
    ) : (
      <IconWrapper size={size}>{leadingIcon}</IconWrapper>
    )
    const endIcon = loading ? null : <IconWrapper size={size}>{trailingIcon}</IconWrapper>
    const showText = !loading || variant === 'link' || variant === 'ghost'

    const renderContent = (childContent: React.ReactNode) => (
      <>
        {startIcon}
        {showText && childContent && (
          <span className="px-1 overflow-hidden text-ellipsis flex-grow whitespace-nowrap">
            {childContent}
          </span>
        )}
        {endIcon}
      </>
    )

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, className }))}
        ref={ref}
        {...props}>
        {asChild && React.isValidElement(children)
          ? React.cloneElement(
              children,
              undefined,
              ...React.Children.toArray(renderContent(children.props.children)),
            )
          : renderContent(children)}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
